import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { HistoryScope, Mailbox } from '@mailpilot/shared';
import { ACCOUNT_COLORS } from '@mailpilot/shared';
import { AnimatePresence, motion } from 'framer-motion';
import clsx from 'clsx';
import { AlertTriangle, Check, ChevronDown, Download, FlaskConical, MoreHorizontal, Pause, Play, RefreshCw, Settings2, Trash2, Unplug, Zap } from 'lucide-react';
import { api, type BodyOf, type ResOf } from '../../lib/api';
import { qk } from '../../lib/keys';
import { fullDate, relativeTime, shortDate } from '../../lib/format';
import { Badge, Button, Card, CardDivider, Field, Input, Menu, Notice, ProgressBar, Switch, useConfirm, useToast, spring } from '../ui';
import { CapabilityList, PROVIDER_LABELS, STATUS_META, healthOf } from './AccountsShared';
import { AccountsHistoryScope } from './AccountsHistoryScope';
import { AccountsDemoIncoming } from './AccountsDemoIncoming';
import { providerLogoFor } from './ProviderLogos';
import { Stat, StatusPill, SubSection, TipIconButton } from './SettingsPrimitives';
import type { useOAuthConnect } from './AccountsOAuth';

type MailboxesRes = ResOf<'GET /api/mailboxes'>;
type PatchBody = BodyOf<'PATCH /api/mailboxes/:id'>;

const PHASE_LABEL: Record<Mailbox['importProgress']['phase'], string> = {
  idle: 'Not started',
  recent: 'Importing recent mail',
  history: 'Importing older history',
  complete: 'Complete',
  paused: 'Paused',
  error: 'Failed',
};

export function useMailboxUpdater() {
  const qc = useQueryClient();
  return (mb: Mailbox) =>
    qc.setQueryData<MailboxesRes>(qk.mailboxes, (prev) => (prev ? { ...prev, mailboxes: prev.mailboxes.map((m) => (m.id === mb.id ? mb : m)) } : prev));
}

export function AccountsMailboxCard({ mb, oauth }: { mb: Mailbox; oauth: ReturnType<typeof useOAuthConnect> }) {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const update = useMailboxUpdater();
  const [open, setOpen] = useState(false);
  const [displayName, setDisplayName] = useState(mb.displayName);
  const [senderName, setSenderName] = useState(mb.senderName);

  useEffect(() => setDisplayName(mb.displayName), [mb.displayName]);
  useEffect(() => setSenderName(mb.senderName), [mb.senderName]);

  const invalidate = () => void qc.invalidateQueries({ queryKey: qk.mailboxes });

  const patch = useMutation({
    mutationFn: (body: PatchBody) => api('PATCH /api/mailboxes/:id', { params: { id: mb.id }, body }),
    onSuccess: (res, body) => {
      update(res.mailbox);
      if (body.isDefaultSender) invalidate(); // others lose the flag
    },
    onError: (e) => toast.error(e),
  });

  const automation = useMutation({
    mutationFn: (enabled: boolean) => api('POST /api/mailboxes/:id/automation', { params: { id: mb.id }, body: { enabled } }),
    onSuccess: (res, enabled) => {
      update(res.mailbox);
      toast.success(enabled ? `Automatic responses on for ${mb.email}` : `Automatic responses off for ${mb.email}`);
    },
    onError: (e) => toast.error(e),
  });

  const sync = useMutation({
    mutationFn: () => api('POST /api/mailboxes/:id/sync', { params: { id: mb.id } }),
    onSuccess: () => {
      toast.success('Sync started');
      setTimeout(invalidate, 2500);
    },
    onError: (e) => toast.error(e),
  });

  const importAction = useMutation({
    mutationFn: (action: 'start' | 'pause' | 'resume') => api('POST /api/mailboxes/:id/import', { params: { id: mb.id }, body: { action } }),
    onSuccess: (res) => update(res.mailbox),
    onError: (e) => toast.error(e),
  });

  const disconnect = useMutation({
    mutationFn: (purge: boolean) => api('DELETE /api/mailboxes/:id', { params: { id: mb.id }, query: purge ? { purge: '1' } : {} }),
    onSuccess: (_r, purge) => {
      invalidate();
      void qc.invalidateQueries({ queryKey: qk.threadsRoot });
      toast.success(purge ? `${mb.email} disconnected and its data deleted` : `${mb.email} disconnected`);
    },
    onError: (e) => toast.error(e),
  });

  const askDisconnect = async (purge: boolean) => {
    const ok = await confirm({
      title: purge ? `Disconnect ${mb.email} and delete its data?` : `Disconnect ${mb.email}?`,
      message: purge
        ? 'MailPilot stops syncing and permanently deletes the imported mail, drafts and attachments for this mailbox from its servers. Your mail at the provider is not touched. This cannot be undone.'
        : 'MailPilot stops syncing and sending from this mailbox and removes its stored credentials. Imported mail stays in MailPilot until you delete it.',
      confirmLabel: purge ? 'Disconnect & delete' : 'Disconnect',
      danger: true,
    });
    if (ok) disconnect.mutate(purge);
  };

  const saveName = (field: 'displayName' | 'senderName', value: string) => {
    const v = value.trim();
    if (!v || v === mb[field]) return;
    patch.mutate({ [field]: v });
  };

  const ip = mb.importProgress;
  const running = ip.phase === 'recent' || ip.phase === 'history';
  const showProgress = running || ip.phase === 'paused' || ip.phase === 'error';
  const importPct = ip.estimatedTotal ? Math.min(100, Math.round((ip.processed / ip.estimatedTotal) * 100)) : null;
  const canReconnect = mb.provider === 'gmail' || mb.provider === 'outlook';
  const canSync = mb.status !== 'needs_reauth' && mb.status !== 'disconnected';
  const status = STATUS_META[mb.status];
  const health = healthOf(mb);
  const busy = disconnect.isPending;

  return (
    <Card className="overflow-hidden">
      {/* Identity row */}
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3 px-6 pt-6">
        <div className="flex min-w-0 items-center gap-3.5">
          <span className="relative shrink-0">
            <span className="flex h-12 w-12 items-center justify-center rounded-[14px] bg-raised ring-1 ring-line/60">
              {mb.isDemo ? <FlaskConical className="h-6 w-6 text-info-ink" aria-hidden /> : providerLogoFor(mb.provider, mb.email)}
            </span>
            <span className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full ring-2 ring-surface" style={{ background: mb.color }} aria-hidden title="Account colour" />
          </span>
          <div className="min-w-0">
            <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
              <h3 className="truncate text-[0.9375rem] font-semibold leading-snug text-ink">{mb.displayName || mb.email}</h3>
              {mb.isDemo && <Badge tone="info">Demo</Badge>}
              {mb.isDefaultSender && <Badge tone="primary">Default sender</Badge>}
            </div>
            <p className="mt-0.5 truncate text-[0.8125rem] text-muted">
              {mb.email} <span className="text-subtle">·</span> {PROVIDER_LABELS[mb.provider]}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:ml-auto">
          <StatusPill tone={status.tone} pulse={mb.status === 'connecting'} title={health.label}>
            {status.label}
          </StatusPill>
          <div className="ml-1 flex items-center gap-0.5 rounded-xl bg-raised/70 p-0.5">
            <TipIconButton
              size="sm"
              label="Sync now"
              icon={<RefreshCw className={clsx('h-4 w-4', sync.isPending && 'animate-spin')} aria-hidden />}
              disabled={!canSync || sync.isPending}
              onClick={() => sync.mutate()}
            />
            <TipIconButton size="sm" label={open ? 'Hide account settings' : 'Account settings'} active={open} aria-expanded={open} aria-controls={`mailbox-settings-${mb.id}`} icon={<Settings2 className="h-4 w-4" aria-hidden />} onClick={() => setOpen((o) => !o)} />
            <Menu
              label={`More actions for ${mb.email}`}
              align="right"
              width={260}
              trigger={(p) => <TipIconButton size="sm" label="More actions" tipPlacement="bottom-end" icon={<MoreHorizontal className="h-4 w-4" aria-hidden />} disabled={busy} {...p} />}
              items={[
                { key: 'disconnect', label: 'Disconnect', description: 'Stop syncing, keep imported mail', icon: <Unplug className="h-4 w-4 text-muted" aria-hidden />, onSelect: () => void askDisconnect(false) },
                { key: 'purge', label: 'Disconnect & delete data', description: 'Erase everything imported from it', icon: <Trash2 className="h-4 w-4" aria-hidden />, danger: true, onSelect: () => void askDisconnect(true) },
              ]}
            />
          </div>
        </div>
      </div>

      {/* Attention */}
      {mb.status === 'needs_reauth' && (
        <div className="px-6 pt-5">
          <Notice tone="warn" icon={<AlertTriangle className="h-4 w-4" aria-hidden />}>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="font-semibold">Reconnect {mb.email}</p>
                <p className="mt-0.5">
                  {mb.statusReason ?? 'The provider authorisation expired or was revoked.'} Sync and automatic responses are paused until you reconnect.
                  {!canReconnect && ' Disconnect it and add it again with a new app password.'}
                </p>
              </div>
              {canReconnect && (
                <Button size="sm" variant="primary" className="shrink-0" loading={oauth.pending === mb.provider} onClick={() => void oauth.start(mb.provider as 'gmail' | 'outlook', mb.id)}>
                  Reconnect
                </Button>
              )}
            </div>
          </Notice>
        </div>
      )}
      {mb.status !== 'needs_reauth' && mb.statusReason && (
        <div className="px-6 pt-5">
          <Notice tone={mb.status === 'error' ? 'danger' : 'info'} icon={mb.status === 'error' ? <AlertTriangle className="h-4 w-4" aria-hidden /> : undefined}>
            {mb.statusReason}
          </Notice>
        </div>
      )}

      {/* Sync & import meta */}
      <div className="px-6 pb-5 pt-5">
        <div className="grid gap-x-6 gap-y-4 sm:grid-cols-3">
          <Stat label="Last sync" value={mb.lastSyncAt ? relativeTime(mb.lastSyncAt) : 'Never'} hint={health.tone === 'warn' ? <span className="text-warn-ink">{health.label}</span> : mb.lastSyncAt ? fullDate(mb.lastSyncAt) : 'Waiting for the first sync'} />
          <Stat
            label="History import"
            value={ip.phase === 'complete' ? `${ip.processed.toLocaleString()} messages` : PHASE_LABEL[ip.phase]}
            hint={
              ip.phase === 'complete'
                ? ip.oldestImportedAt
                  ? `${shortDate(ip.oldestImportedAt)} – ${shortDate(ip.newestImportedAt)}`
                  : 'Everything imported'
                : ip.phase === 'idle'
                  ? 'Older mail is not imported yet'
                  : `${ip.processed.toLocaleString()}${ip.estimatedTotal ? ` of ~${ip.estimatedTotal.toLocaleString()}` : ''} messages${importPct !== null ? ` · ${importPct}%` : ''}`
            }
          />
          <Stat label="Automation" value={mb.automationActivatedAt ? 'On' : 'Off'} hint={mb.automationActivatedAt ? `Since ${shortDate(mb.automationActivatedAt)}` : 'Suggestions only'} />
        </div>

        {(showProgress || ip.phase === 'idle' || ip.phase === 'complete') && (
          <div className="mt-4 flex flex-wrap items-center gap-3">
            {showProgress && (
              <div className="min-w-[12rem] flex-1">
                <ProgressBar value={ip.processed} max={ip.estimatedTotal} label={`Import progress for ${mb.email}`} />
              </div>
            )}
            {ip.error && <p className="w-full text-xs text-danger-ink">{ip.error}</p>}
            <div className="flex flex-wrap gap-2 sm:ml-auto">
              {running && (
                <Button size="sm" icon={<Pause className="h-4 w-4" aria-hidden />} loading={importAction.isPending} onClick={() => importAction.mutate('pause')}>
                  Pause import
                </Button>
              )}
              {ip.phase === 'paused' && (
                <Button size="sm" icon={<Play className="h-4 w-4" aria-hidden />} loading={importAction.isPending} onClick={() => importAction.mutate('resume')}>
                  Resume import
                </Button>
              )}
              {(ip.phase === 'idle' || ip.phase === 'error') && (
                <Button size="sm" variant={ip.phase === 'idle' ? 'soft' : 'secondary'} icon={<Download className="h-4 w-4" aria-hidden />} loading={importAction.isPending} disabled={!canSync} onClick={() => importAction.mutate('start')}>
                  {ip.phase === 'idle' ? 'Import older mail' : 'Retry import'}
                </Button>
              )}
              {ip.phase === 'complete' && (
                <Button size="sm" variant="ghost" icon={<RefreshCw className="h-4 w-4" aria-hidden />} loading={importAction.isPending} disabled={!canSync} onClick={() => importAction.mutate('start')}>
                  Re-run import
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      <CardDivider />

      {/* Automation */}
      <div className="flex items-start justify-between gap-6 px-6 py-5">
        <div className="flex min-w-0 items-start gap-3">
          <span className={clsx('mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px]', mb.automationActivatedAt ? 'bg-primary-soft text-primary-ink' : 'bg-sunken text-muted')}>
            <Zap className="h-4 w-4" aria-hidden />
          </span>
          <div className="min-w-0">
            <label htmlFor={`automation-${mb.id}`} className="cursor-pointer font-medium text-ink">
              Automatic responses
            </label>
            <p className="mt-0.5 text-[0.8125rem] leading-relaxed text-muted">
              {mb.automationActivatedAt ? <span className="text-ink">Activated {fullDate(mb.automationActivatedAt)}. </span> : null}
              Only mail received after activation is eligible for acknowledgements or automatic replies. Historical imports never trigger replies.
            </p>
          </div>
        </div>
        <Switch id={`automation-${mb.id}`} checked={!!mb.automationActivatedAt} disabled={automation.isPending} onChange={(v) => automation.mutate(v)} />
      </div>

      {mb.isDemo && (
        <>
          <CardDivider />
          <div className="px-6 py-5">
            <AccountsDemoIncoming mb={mb} />
          </div>
        </>
      )}

      <CardDivider />

      {/* Expandable settings */}
      <button
        type="button"
        aria-expanded={open}
        aria-controls={`mailbox-settings-${mb.id}`}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-3 px-6 py-4 text-left transition-colors hover:bg-raised/60"
      >
        <span className="flex items-center gap-3">
          <span className="flex h-8 w-8 items-center justify-center rounded-[10px] bg-sunken text-muted">
            <Settings2 className="h-4 w-4" aria-hidden />
          </span>
          <span>
            <span className="block text-[0.875rem] font-medium text-ink">Account settings</span>
            <span className="block text-[0.75rem] text-muted">Name, sender identity, colour, history scope and capabilities</span>
          </span>
        </span>
        <ChevronDown className={clsx('h-4 w-4 shrink-0 text-muted transition-transform', open && 'rotate-180')} aria-hidden />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div id={`mailbox-settings-${mb.id}`} initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={spring} className="overflow-hidden">
            <CardDivider />
            <div className="space-y-6 px-6 py-6">
              <SubSection title="Identity" hint="How this account appears in MailPilot and to recipients.">
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Account name" hint="Shown in the sidebar and badges.">
                    {(id) => <Input id={id} value={displayName} onChange={(e) => setDisplayName(e.target.value)} onBlur={() => saveName('displayName', displayName)} />}
                  </Field>
                  <Field label="Sender name" hint="The name recipients see in the From line.">
                    {(id) => <Input id={id} value={senderName} onChange={(e) => setSenderName(e.target.value)} onBlur={() => saveName('senderName', senderName)} />}
                  </Field>
                </div>
                <div>
                  <p className="mb-2 text-[0.8125rem] font-medium text-ink">Colour</p>
                  <div role="radiogroup" aria-label={`Colour for ${mb.email}`} className="flex flex-wrap gap-2">
                    {ACCOUNT_COLORS.map((c) => {
                      const active = c.toLowerCase() === mb.color.toLowerCase();
                      return (
                        <button
                          key={c}
                          type="button"
                          role="radio"
                          aria-checked={active}
                          aria-label={`Colour ${c}`}
                          onClick={() => !active && patch.mutate({ color: c })}
                          className={clsx('flex h-8 w-8 items-center justify-center rounded-full ring-offset-2 ring-offset-surface transition-[box-shadow,transform] hover:scale-105', active ? 'ring-2 ring-ink' : 'hover:ring-2 hover:ring-line')}
                          style={{ background: c }}
                        >
                          {active && <Check className="h-4 w-4 text-white" aria-hidden />}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="flex items-start justify-between gap-6">
                  <div className="min-w-0">
                    <label htmlFor={`default-sender-${mb.id}`} className="font-medium text-ink">
                      Default sender for new emails
                    </label>
                    <p className="mt-0.5 text-[0.8125rem] leading-relaxed text-muted">Replies always go out from the account that received the message unless you change the sender.</p>
                  </div>
                  <Switch id={`default-sender-${mb.id}`} checked={mb.isDefaultSender} disabled={patch.isPending || mb.isDefaultSender} onChange={(v) => v && patch.mutate({ isDefaultSender: true })} />
                </div>
              </SubSection>

              <CardDivider className="!mx-0" />

              <AccountsHistoryScope mb={mb} onSave={(historyScope: HistoryScope) => patch.mutate({ historyScope })} saving={patch.isPending} />

              <CardDivider className="!mx-0" />

              <SubSection title="Capabilities & limitations" hint={`What MailPilot can do with ${PROVIDER_LABELS[mb.provider]}.`}>
                <CapabilityList caps={mb.capabilities} />
              </SubSection>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}
